import { TreeNode } from 'primeng/api';

/**
 * Generic helper function to build tree from nested structure (with childs property)
 * @param items Array of items with nested children in 'childs' property
 * @param options Optional configuration with labelFormatter function
 * @returns Array of TreeNode objects for PrimeNG TreeSelect
 */
export function buildTreeFromNested(items: any[], options?: { labelFormatter?: (item: any) => string; }): TreeNode[] {
    if (!items || items.length === 0) {
        return [];
    }

    const { labelFormatter = (item: any) => item.name } = options || {};

    const buildNode = (item: any): TreeNode => {
        const node: TreeNode = {
            key: item.id,
            data: item,
            label: labelFormatter(item),
            children: []
        };

        // If this item has children, recursively build them
        if (item.childs && item.childs.length > 0) {
            node.children = item.childs.map((child: any) => buildNode(child));
        }

        return node;
    };

    return items.map(item => buildNode(item));
}

/**
 * Generic helper function to build tree structure from flat array
 * @param items Flat array of items with parentId relationships
 * @param options Optional configuration with labelFormatter function and expanded state
 * @returns Array of TreeNode objects for PrimeNG TreeSelect
 */
export function buildTree(items: any[], options?: { labelFormatter?: (item: any) => string; expanded?: boolean; }): TreeNode[] {
    if (!items || items.length === 0) {
        return [];
    }

    const { labelFormatter = (item: any) => item.name, expanded = false } = options || {};

    // Create a map of items by id for quick lookup
    const itemMap = new Map<string, TreeNode>();

    // First create all tree nodes
    items.forEach(item => {
        const node: TreeNode = {
            key: item.id,
            data: item,
            label: labelFormatter(item),
            children: [],
            expanded: expanded
        };
        itemMap.set(item.id, node);
    });

    // Then build the hierarchy
    const rootNodes: TreeNode[] = [];
    items.forEach(item => {
        const node = itemMap.get(item.id);
        if (!node) return;

        if (item.parentId && itemMap.has(item.parentId)) {
            // This is a child node
            const parentNode = itemMap.get(item.parentId);
            if (parentNode && parentNode.children) {
                parentNode.children.push(node);
            }
        } else {
            // This is a root node
            rootNodes.push(node);
        }
    });

    return rootNodes;
}
